export function formatPatientName(patientName) {
  if (!patientName || patientName.length === 0) {
    return '王*';
  }
  if (patientName.length === 2 || patientName.length === 3) {
    return patientName.charAt(0) + '*'.repeat(patientName.length - 1);
  } else if (patientName.length >= 4) {
    return patientName.substring(0, 2) + '*'.repeat(patientName.length - 2);
  } else {
    return patientName;
  }
}

export function formatTherapistName(name) {
  if (!name) return '';
  return String(name).replace(/\s*[（(]满级[）)]\s*/g, '').trim();
}

export function formatTimeDisplay(timeStr) {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) return timeStr;
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function parseTimeString(timeStr) {
  if (!timeStr) return null;
  if (timeStr.includes('T') || timeStr.includes('-')) {
    const date = new Date(timeStr);
    return isNaN(date.getTime()) ? null : date;
  }
  const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    const today = new Date();
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      today.setHours(hours, minutes, 0, 0);
      return today;
    }
  }
  return null;
}

export function parseTimeStringWithDate(timeStr, appointmentDate) {
  if (!timeStr || !appointmentDate) return null;
  if (timeStr.includes('T') || timeStr.includes('-')) {
    const date = new Date(timeStr);
    return isNaN(date.getTime()) ? null : date;
  }
  const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const d = new Date(appointmentDate);
    d.setHours(hours, minutes, 0, 0);
    return d;
  }
  return null;
}

export function getSleepStatus(item) {
  if (item.status === 'completed') {
    return '';
  }
  if (item.realtime_sleep_state) {
    return item.realtime_sleep_state;
  }
  return '清醒';
}

/**
 * 睡眠视图人物立绘缓存（默认形象 + 自选皮肤懒加载）。
 *
 * 默认规则（用户未选择自选形象时）：
 * - 使用 assets/men、assets/women 下「原版」男女图。
 * - 男性：深睡 Male.png / 浅睡 半眯男.png / 清醒 睁眼男.png
 * - 女性：深睡 闭眼女.png / 浅睡 半眯女.png / 清醒 睁眼女.png
 *
 * 自选形象：
 * - 皮肤目录 assets/skins/{skinId}/，取值见 VALID_SKIN_IDS
 * - 表情映射：深睡→闭眼、浅睡→半眯、清醒→睁眼
 * - 大屏 item.patientAvatarSkinId（或 avatarSkinId）有值时优先用皮肤；否则默认原版
 * - 详见 assets/skins/README.md
 *
 * 资源约定：
 * - PNG 必须为透明背景；入库后最长边约 400（与压缩策略一致）
 */
const imageCache = {
  // 默认男性图片（按睡眠状态）
  'male-深睡': null,
  'male-浅睡': null,
  'male-清醒': null,
  'male-default': null,
  // 默认女性图片（按睡眠状态）
  'female-深睡': null,
  'female-浅睡': null,
  'female-清醒': null,
  'female-default': null,
  // 状态图标
  'status-已结束': null,
  'status-治疗中': null
};

/** 自选皮肤静态资源表（webpack 需静态路径，不可动态拼接 require） */
const SKIN_IMAGE_MAP = {
  'atu-深睡': () => require('@/assets/skins/atu/闭眼.png'),
  'atu-浅睡': () => require('@/assets/skins/atu/半眯.png'),
  'atu-清醒': () => require('@/assets/skins/atu/睁眼.png'),
  'handsome-深睡': () => require('@/assets/skins/handsome/闭眼.png'),
  'handsome-浅睡': () => require('@/assets/skins/handsome/半眯.png'),
  'handsome-清醒': () => require('@/assets/skins/handsome/睁眼.png'),
  'warrior-深睡': () => require('@/assets/skins/warrior/闭眼.png'),
  'warrior-浅睡': () => require('@/assets/skins/warrior/半眯.png'),
  'warrior-清醒': () => require('@/assets/skins/warrior/睁眼.png'),
  'dancer-深睡': () => require('@/assets/skins/dancer/闭眼.png'),
  'dancer-浅睡': () => require('@/assets/skins/dancer/半眯.png'),
  'dancer-清醒': () => require('@/assets/skins/dancer/睁眼.png'),
  'oldfun-深睡': () => require('@/assets/skins/oldfun/闭眼.png'),
  'oldfun-浅睡': () => require('@/assets/skins/oldfun/半眯.png'),
  'oldfun-清醒': () => require('@/assets/skins/oldfun/睁眼.png'),
  'lionboy-深睡': () => require('@/assets/skins/lionboy/闭眼.png'),
  'lionboy-浅睡': () => require('@/assets/skins/lionboy/半眯.png'),
  'lionboy-清醒': () => require('@/assets/skins/lionboy/睁眼.png'),
  'xiaomei-深睡': () => require('@/assets/skins/xiaomei/闭眼.png'),
  'xiaomei-浅睡': () => require('@/assets/skins/xiaomei/半眯.png'),
  'xiaomei-清醒': () => require('@/assets/skins/xiaomei/睁眼.png'),
  'elegant-深睡': () => require('@/assets/skins/elegant/闭眼.png'),
  'elegant-浅睡': () => require('@/assets/skins/elegant/半眯.png'),
  'elegant-清醒': () => require('@/assets/skins/elegant/睁眼.png'),
  'sweet-深睡': () => require('@/assets/skins/sweet/闭眼.png'),
  'sweet-浅睡': () => require('@/assets/skins/sweet/半眯.png'),
  'sweet-清醒': () => require('@/assets/skins/sweet/睁眼.png'),
  'bookworm-深睡': () => require('@/assets/skins/bookworm/闭眼.png'),
  'bookworm-浅睡': () => require('@/assets/skins/bookworm/半眯.png'),
  'bookworm-清醒': () => require('@/assets/skins/bookworm/睁眼.png'),
  'coach-深睡': () => require('@/assets/skins/coach/闭眼.png'),
  'coach-浅睡': () => require('@/assets/skins/coach/半眯.png'),
  'coach-清醒': () => require('@/assets/skins/coach/睁眼.png'),
  'gourmet-深睡': () => require('@/assets/skins/gourmet/闭眼.png'),
  'gourmet-浅睡': () => require('@/assets/skins/gourmet/半眯.png'),
  'gourmet-清醒': () => require('@/assets/skins/gourmet/睁眼.png')
};

const VALID_SKIN_IDS = new Set([
  'atu', 'handsome', 'warrior', 'dancer', 'oldfun', 'lionboy',
  'xiaomei', 'elegant', 'sweet', 'bookworm', 'coach', 'gourmet'
]);

/**
 * 初始化默认人物/状态图标缓存（只执行一次，避免重复 require）。
 * 自选皮肤在 getCharacterImage 中按需懒加载，减轻启动压力。
 */
function initImageCache() {
  if (imageCache['male-深睡'] !== null) {
    return; // 已经初始化过
  }
  
  try {
    // 默认男性：深睡 / 浅睡 / 清醒（原版透明底）
    imageCache['male-深睡'] = require('@/assets/men/Male.png');
    imageCache['male-浅睡'] = require('@/assets/men/半眯男.png');
    imageCache['male-清醒'] = require('@/assets/men/睁眼男.png');
    imageCache['male-default'] = require('@/assets/men/Male.png');
    
    // 默认女性：深睡 / 浅睡 / 清醒（原版透明底）
    imageCache['female-深睡'] = require('@/assets/women/闭眼女.png');
    imageCache['female-浅睡'] = require('@/assets/women/半眯女.png');
    imageCache['female-清醒'] = require('@/assets/women/睁眼女.png');
    imageCache['female-default'] = require('@/assets/women/闭眼女.png');
    
    imageCache['status-已结束'] = require('@/assets/icon_治疗完成.png');
    imageCache['status-治疗中'] = require('@/assets/icon_治疗中.png');
  } catch (error) {
    logger.warn('图片缓存初始化失败:', error);
  }
}

// 初始化缓存
initImageCache();

/**
 * 解析预约 item 上的皮肤 ID（兼容 snake/camel 转换后的字段名）。
 */
function resolveAvatarSkinId(item) {
  if (!item) return null;
  const raw = item.patientAvatarSkinId || item.avatarSkinId || item.patient_avatar_skin_id || item.avatar_skin_id;
  if (raw == null) return null;
  const id = String(raw).trim();
  if (!id || !VALID_SKIN_IDS.has(id)) return null;
  return id;
}

/**
 * 按需加载并缓存自选皮肤表情图。
 */
function getSkinImage(skinId, sleepStatus) {
  const key = `${skinId}-${sleepStatus}`;
  if (imageCache[key] != null) {
    return imageCache[key];
  }
  const loader = SKIN_IMAGE_MAP[key];
  if (!loader) {
    return null;
  }
  try {
    imageCache[key] = loader();
    return imageCache[key];
  } catch (error) {
    logger.warn('自选皮肤图片加载失败:', key, error);
    return null;
  }
}

/**
 * 按患者自选皮肤（优先）或性别 + 睡眠状态返回人物立绘。
 * patientGender === 1 为男，否则为女；睡眠状态由 getSleepStatus 计算。
 */
export function getCharacterImage(item) {
  if (!item) {
    return imageCache['male-default'] || require('@/assets/men/Male.png');
  }
  const sleepStatus = getSleepStatus(item);
  const skinId = resolveAvatarSkinId(item);
  if (skinId) {
    const skinImg = getSkinImage(skinId, sleepStatus);
    if (skinImg) {
      return skinImg;
    }
  }

  const gender = (item.patientGender === 1) ? 'male' : 'female';
  const cacheKey = `${gender}-${sleepStatus}`;
  
  // 从缓存中获取，如果不存在则使用默认值
  const cachedImage = imageCache[cacheKey];
  if (cachedImage !== null) {
    return cachedImage;
  }
  
  // 如果缓存中没有，使用默认值并更新缓存
  const defaultKey = `${gender}-default`;
  return imageCache[defaultKey] || require('@/assets/men/Male.png');
}

export function getTreatmentStatus(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return '无数据';
  }
  if (appointment.isHistorical || appointment.treatmentStatus === '已结束') {
    return '已结束';
  }
  const now = new Date();
  let appointmentDate;
  if (appointment.appointmentDate) {
    appointmentDate = new Date(appointment.appointmentDate);
  } else {
    const startTimeDate = new Date(appointment.startTime);
    if (!isNaN(startTimeDate.getTime())) {
      appointmentDate = startTimeDate;
    } else {
      appointmentDate = new Date();
    }
  }
  const appointmentDateOnly = new Date(appointmentDate);
  appointmentDateOnly.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (appointmentDateOnly.getTime() < today.getTime()) {
    return '已结束';
  }
  if (appointmentDateOnly.getTime() > today.getTime()) {
    return '治疗未开始';
  }
  const startTime = parseTimeStringWithDate(appointment.startTime, appointmentDateOnly);
  const endTime = parseTimeStringWithDate(appointment.endTime, appointmentDateOnly);
  if (!startTime || !endTime) {
    return '时间格式错误';
  }
  if (now >= endTime) {
    return '已结束';
  }
  if (now < startTime) {
    return '治疗未开始';
  }
  return '治疗中';
}

export function getTreatmentStatusClass(item) {
  const status = getTreatmentStatus(item);
  if (status === '已结束' || status === '治疗即将完成') {
    return 'treatment-completed';
  } else {
    return 'treatment-ongoing';
  }
}

export function getTreatmentStatusIcon(item) {
  const status = getTreatmentStatus(item);
  const cacheKey = (status === '已结束' || status === '治疗即将完成') 
    ? 'status-已结束' 
    : 'status-治疗中';
  
  // 从缓存中获取
  const cachedIcon = imageCache[cacheKey];
  if (cachedIcon !== null) {
    return cachedIcon;
  }
  
  // 如果缓存中没有，fallback到require
  if (status === '已结束' || status === '治疗即将完成') {
    return require('@/assets/icon_治疗完成.png');
  } else {
    return require('@/assets/icon_治疗中.png');
  }
}

export function getBrainwaveDisplay(item, type) {
  if (!item) return 0;
  if (getTreatmentStatus(item) === '已结束') return 0;
  const v = item[type];
  return Math.round(v || 0);
}

export function generatePatientSeed(item) {
  const seedString = `${item.patientName || ''}_${item.roomName || ''}_${item.startTime || ''}`;
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    const char = seedString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

export function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

export function calculateEnergyScore(item) {
  if (!item || !item.startTime || !item.endTime) {
    return 0;
  }
  const now = new Date();
  const startTime = parseTimeString(item.startTime);
  const endTime = parseTimeString(item.endTime);
  if (!startTime) {
    return 0;
  }
  if (getTreatmentStatus(item) === '已结束') {
    const treatmentSeconds = Math.floor((endTime - startTime) / 1000);
    if (item.finalScore !== undefined) {
      return item.finalScore;
    }
    let totalScore = 0;
    const sleepStatus = getSleepStatus(item);
    const baseSeed = generatePatientSeed(item);
    for (let i = 0; i < treatmentSeconds; i++) {
      const seed = baseSeed + i;
      const random = seededRandom(seed) * 100;
      let scoreToAdd = 1;
      switch (sleepStatus) {
        case '深睡':
          if (random < 60) {
            scoreToAdd = 3;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 1;
          }
          break;
        case '浅睡':
          if (random < 30) {
            scoreToAdd = 1;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 3;
          }
          break;
        case '清醒':
          if (random < 90) {
            scoreToAdd = 1;
          } else {
            scoreToAdd = 2;
          }
          break;
      }
      totalScore += scoreToAdd;
    }
    item.finalScore = totalScore;
    return totalScore;
  }
  const treatmentSeconds = Math.floor((now - startTime) / 1000);
  if (!item.energyScore) {
    item.energyScore = 0;
    item.lastCalculatedSecond = 0;
  }
  const newSeconds = treatmentSeconds - (item.lastCalculatedSecond || 0);
  if (newSeconds > 0) {
    const sleepStatus = getSleepStatus(item);
    const baseSeed = generatePatientSeed(item);
    for (let i = 0; i < newSeconds; i++) {
      const currentSecond = (item.lastCalculatedSecond || 0) + i;
      const seed = baseSeed + currentSecond;
      const random = seededRandom(seed) * 100;
      let scoreToAdd = 1;
      switch (sleepStatus) {
        case '深睡':
          if (random < 60) {
            scoreToAdd = 3;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 1;
          }
          break;
        case '浅睡':
          if (random < 30) {
            scoreToAdd = 1;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 3;
          }
          break;
        case '清醒':
          if (random < 90) {
            scoreToAdd = 1;
          } else {
            scoreToAdd = 2;
          }
          break;
      }
      item.energyScore += scoreToAdd;
      if (i === newSeconds - 1) {
        item.lastAddedScore = scoreToAdd;
      }
    }
    item.lastCalculatedSecond = treatmentSeconds;
  }
  return item.energyScore;
}

export function getProgressPercentage(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return 0;
  }
  const startTime = parseTimeString(appointment.startTime);
  const endTime = parseTimeString(appointment.endTime);
  if (!startTime || !endTime) {
    return 0;
  }
  const totalSeconds = Math.floor((endTime - startTime) / 1000);
  const score = calculateEnergyScore(appointment);
  const percentage = Math.floor((score / totalSeconds) * 100);
  appointment.totalSeconds = totalSeconds;
  if (getTreatmentStatus(appointment) === '已结束') {
    if (appointment.finalPercentage === undefined) {
      const randomFinal = 100 + Math.floor(Math.random() * 51);
      appointment.finalPercentage = randomFinal;
      return randomFinal;
    }
    return appointment.finalPercentage;
  }
  return percentage;
}

export function getSleepBubbleValue(sleepStatus) {
  const random = Math.random() * 100;
  let bubbleValue = 1;
  switch (sleepStatus) {
    case '深睡':
      if (random < 60) {
        bubbleValue = 3;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 1;
      }
      break;
    case '浅睡':
      if (random < 30) {
        bubbleValue = 1;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 3;
      }
      break;
    case '清醒':
      if (random < 90) {
        bubbleValue = 1;
      } else {
        bubbleValue = 2;
      }
      break;
    default:
      bubbleValue = 1;
  }
  return bubbleValue;
}
